import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import dayjs from 'dayjs';

import { restApi } from '~features/api/restApiSlice';
import { AGGREGATED_METRICS_API_SHARED_SETTINGS } from '~features/api/aggregatedMetricsApiSettings';
import {
  type CrawlerCategoryScheme,
  categorizeCrawlersData,
  deCategorizeCrawlersList,
} from '~features/crawler-filter';

type DashboardV3ApiQueryParams = Omit<AggregatedMetrics.PagesRenderedQueryParams, 'dateFrom' | 'dateTo'> & {
  dateFrom: string;
  dateTo: string;
};

function toWholeDayWindow(dateFrom: Date | string, dateTo: Date | string) {
  return {
    dateFrom: dayjs(dateFrom).startOf('day').toISOString(),
    dateTo: dayjs(dateTo).endOf('day').toISOString(),
  };
}

type WithCategoryScheme = { categoryScheme: CrawlerCategoryScheme };

type GraphQueryParams = AggregatedMetrics.BaseGraphQueryParams & WithCategoryScheme;

export function buildParams(
  input: (AggregatedMetrics.BaseGraphQueryParams | AggregatedMetrics.PagesRenderedQueryParams) & WithCategoryScheme
) {
  const output: DashboardV3ApiQueryParams = {
    ...toWholeDayWindow(input.dateFrom, input.dateTo),
    firstDayOfWeek: dayjs().localeData().firstDayOfWeek(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // only apply filters if there are values
  if (input.domain) output.domain = input.domain;
  if (input.crawlers && input.crawlers.length > 0) {
    output.crawlers = deCategorizeCrawlersList(input.crawlers, input.categoryScheme);
  }
  if ('includeScheduledRenders' in input && input.includeScheduledRenders === false) {
    output.includeScheduledRenders = false;
  }

  return output;
}

export function categorizePagesCrawled(
  response: AggregatedMetrics.PagesCrawledResult,
  scheme: CrawlerCategoryScheme
): AggregatedMetrics.PagesCrawledResult {
  return { ...response, data: categorizeCrawlersData(response.data, scheme) };
}

export const PAGES_CRAWLED_ENDPOINT = '/v4/aggregated-metrics/pages-crawled';

const dashboardV3Api = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getPagesCrawled: builder.query<AggregatedMetrics.PagesCrawledResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: PAGES_CRAWLED_ENDPOINT, params };
      },
      transformResponse: (response: AggregatedMetrics.PagesCrawledResult, _meta, arg) =>
        categorizePagesCrawled(response, arg.categoryScheme),
    }),
    hasCrawlTraffic: builder.query<
      AggregatedMetrics.HasCrawlTrafficResult,
      { dateFrom: Date; dateTo: Date; domain?: string }
    >({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params: { dateFrom: string; dateTo: string; domain?: string } = toWholeDayWindow(
          input.dateFrom,
          input.dateTo
        );

        if (input.domain) params.domain = input.domain;

        return { url: '/v4/aggregated-metrics/crawl-traffic/exists', params };
      },
    }),

    getPagesCrawledSummary: builder.query<AggregatedMetrics.PagesCrawledSummaryResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/pages-crawled-summary', params };
      },
    }),

    getStatusCodes: builder.query<AggregatedMetrics.StatusCodesResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/status-codes', params };
      },
    }),

    getCacheHitsRatioSummary: builder.query<AggregatedMetrics.CacheHitsRatioSummaryResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/cache-hits-ratio-summary', params };
      },
    }),

    getErrorRateSummary: builder.query<AggregatedMetrics.ErrorRateSummaryResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/error-rate-summary', params };
      },
    }),

    getAverageCrawlTimeSummary: builder.query<AggregatedMetrics.DeliveryTimeSummaryResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/delivery-times-summary', params };
      },
    }),

    getAverageCrawlTime: builder.query<AggregatedMetrics.DeliveryTimeResult, GraphQueryParams>({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/delivery-times', params };
      },
    }),

    getPagesRendered: builder.query<
      AggregatedMetrics.PagesRenderedResult,
      AggregatedMetrics.PagesRenderedQueryParams & WithCategoryScheme
    >({
      ...AGGREGATED_METRICS_API_SHARED_SETTINGS,
      query: (input) => {
        const params = buildParams(input);

        return { url: '/v4/aggregated-metrics/pages-rendered', params };
      },
    }),

    getUsageOverview: builder.query<
      { data: { originalDeliveryTime: number; averageDeliveryTime: number } },
      { days: number; domain?: string }
    >({
      query: ({ days, domain }) => ({
        url: '/v3/aggregated-metrics/usage-overview',
        method: 'GET',
        params: {
          from: `-${days}days`,
          timezoneOffset: new Date().getTimezoneOffset(),
          domain,
        },
      }),
    }),
  }),
});

export const {
  useGetPagesCrawledQuery,
  useLazyGetPagesCrawledQuery,

  useGetPagesCrawledSummaryQuery,
  useLazyGetPagesCrawledSummaryQuery,

  useHasCrawlTrafficQuery,

  useGetStatusCodesQuery,
  useLazyGetStatusCodesQuery,

  useLazyGetAverageCrawlTimeQuery,
  useGetAverageCrawlTimeQuery,

  useGetPagesRenderedQuery,

  useGetCacheHitsRatioSummaryQuery,
  useGetErrorRateSummaryQuery,
  useGetAverageCrawlTimeSummaryQuery,

  useLazyGetUsageOverviewQuery,
} = dashboardV3Api;
